import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const testimonialApi = createApi({
  reducerPath: "testimonialApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Testimonial"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Testimonials
    getTestimonialData: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "display_order",
        order = "asc",
      } = {}) => ({
        url: "/testimonial",
        params: { page, limit, search, sortby: sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        testimonials: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.testimonials?.length
          ? [
              ...result.testimonials.map(({ id }) => ({
                type: "Testimonial",
                id: Number(id),
              })),
              { type: "Testimonial", id: "LIST" },
            ]
          : [{ type: "Testimonial", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get Testimonial By ID
    getTestimonialById: builder.query({
      query: (id) => `/testimonial/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Testimonial", id }],
    }),

    // Create Testimonial
    createTestimonial: builder.mutation({
      query: (data) => ({
        url: "/testimonial",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Testimonial", id: "LIST" }],
    }),

    // Update Testimonial
    updateTestimonial: builder.mutation({
      query: ({ id, data }) => ({
        url: `/testimonial/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Testimonial", id },
        { type: "Testimonial", id: "LIST" },
      ],
    }),

    // Delete Testimonial
    deleteTestimonial: builder.mutation({
      query: (id) => ({
        url: `/testimonial/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Testimonial", id: "LIST" }],
    }),

    /* Toggle Testimonial Status */
    toggleTestimonialStatus: builder.mutation({
      query: (id) => ({
        url: `/testimonial/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Testimonial", id },
        { type: "Testimonial", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetTestimonialDataQuery,
  useGetTestimonialByIdQuery,
  useCreateTestimonialMutation,
  useUpdateTestimonialMutation,
  useDeleteTestimonialMutation,
  useToggleTestimonialStatusMutation,
} = testimonialApi;
